import { TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { IdentityStatus, IdentityStatusChip } from '@verifime/components';
import { EntityStatus, formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import SearchResult, { TSearchResultProps } from './SearchResult';
import { TCustomerIdentity } from './types';

type TTableRowForIdentityProps = Parameters<
  TSearchResultProps<TCustomerIdentity>['TableRowComponent']
>[0];

export type TIdentitySearchResultsProps = Omit<
  TSearchResultProps<TCustomerIdentity>,
  'TableRowComponent' | 'tableHeader'
>;

export const toIdentityStatus = (entityStatus: string): IdentityStatus | undefined => {
  switch (entityStatus) {
    case EntityStatus.InProgress:
      return IdentityStatus.IN_PROGRESS;
    case EntityStatus.Rejected:
      return IdentityStatus.REJECTED;
    case EntityStatus.Verified:
      return IdentityStatus.VERIFIED;
    case EntityStatus.Draft:
      return IdentityStatus.DRAFT;
    case EntityStatus.Empty:
    default:
      return undefined;
  }
};

function TableHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Name</TableCell>
        <TableCell>Created</TableCell>
        <TableCell>Updated</TableCell>
        <TableCell>Entity Status</TableCell>
        <TableCell>Note</TableCell>
      </TableRow>
    </TableHead>
  );
}

function TableRowForIdentity({ result, onRowClick }: TTableRowForIdentityProps) {
  return (
    <TableRow
      sx={{
        cursor: 'pointer',
      }}
      key={result.id}
      data-record-id={result.id}
      data-record-type="identity"
      data-record-status={result.status}
      data-record-note={result.userCustomerAssociationNote}
      data-record-created={result.createdDate}
      data-record-updated={result.updatedDate}
      onClick={() => onRowClick?.(result)}
      hover
    >
      <TableCell>
        {result.name}
        <Typography variant="body2" color="textSecondary">
          {result.email}
        </Typography>
      </TableCell>
      <TableCell>{formatToDDMMMYYYYHHMMAAA(result.createdDate)}</TableCell>
      <TableCell>{formatToDDMMMYYYYHHMMAAA(result.updatedDate)}</TableCell>
      <TableCell>
        <IdentityStatusChip status={toIdentityStatus(result.status)} />
      </TableCell>
      <TableCell>{result.userCustomerAssociationNote}</TableCell>
    </TableRow>
  );
}

export default function IdentitySearchResults(props: TIdentitySearchResultsProps) {
  return (
    <SearchResult<TCustomerIdentity>
      data-testid="identity-search-results"
      tableHeader={<TableHeader />}
      TableRowComponent={TableRowForIdentity}
      {...props}
    />
  );
}
